import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthStore } from '../../lib/auth';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import TokenCostModal from '../../components/TokenCostModal';
import { apiClient } from '../../lib/api';
import { useBrandExtractionStatus } from '../../hooks/useBrandExtractionStatus';

// Type definitions matching backend schema
// Note: API may return flexible formats (strings or objects) due to GPT-4 Vision variability
interface BrandColor {
  hex: string;
  name?: string;
  rgb?: string;
  usage?: number | string; // Can be number (percentage) or string (description)
}

interface Typography {
  family: string;
  weights: number[];
  fallbacks: string[];
  googleFontUrl?: string;
}

interface ToneOfVoice {
  formal: number;
  friendly: number;
  technical: number;
  playful: number;
  professional: number;
  description?: string;
}

interface ImageryGuidelines {
  artDirection: string;
  subjects: string[];
  backgrounds: string[];
  aspectRatios: Array<{
    ratio: string;
    platform: string;
  }>;
  colorTreatment: string;
  filters?: string[];
}

interface ComponentStyles {
  buttons: {
    variants: string[];
    textCase: string;
    padding: string;
    corners: string;
  };
  icons: {
    style: string;
    strokeWidth: string;
    corners: string;
  };
  radius: {
    buttons: string;
    cards: string;
    inputs: string;
  };
  shadows?: {
    elevation: string;
    pattern: string;
  };
  spacing?: {
    gridGap: string;
    sectionPadding: string;
  };
}

interface Gradient {
  type: string; // "linear-gradient", "radial-gradient", "conic-gradient"
  colors: string[]; // Array of HEX codes
  direction?: string; // e.g., "45deg", "to right"
  css: string; // Full CSS gradient string
  usage: string; // e.g., "Hero background", "Button hover"
  stops?: string[]; // e.g., ["0%", "50%", "100%"]
}

interface ExtractionMetadata {
  confidence: number;
  timestamp: string;
  method: string;
  scrapeJobId?: string;
  warnings?: string[];
  siteName?: string;
  tone?: string;
  visualStyle?: string;
  reasoning?: string;
  logoDescription?: string;
  model?: string;
}

interface BrandGuide {
  id: string;
  workspaceId: string;
  version: number;
  isActive: boolean;
  sourceUrl?: string;
  sourceType: string;
  primaryColors?: BrandColor[];
  secondaryColors?: BrandColor[];
  typographyHeading?: Typography | string; // Can be object or string (e.g., "Merriweather, serif")
  typographyBody?: Typography | string; // Can be object or string (e.g., "Figtree, sans-serif")
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  logoFileSize?: number;
  logoMimeType?: string;
  toneOfVoice?: ToneOfVoice | null;
  brandKeywords?: string[];
  companyDescription?: string;
  imageryGuidelines?: ImageryGuidelines | null;
  componentStyles?: ComponentStyles | null;
  gradients?: Gradient[] | null;
  extractionMetadata?: ExtractionMetadata;
  createdAt: string;
  updatedAt: string;
}

// Validation schema for inline editing
const brandGuideSchema = z.object({
  companyDescription: z.string().min(20).max(600).optional(),
  brandKeywords: z.array(z.string()).optional(),
});

type BrandGuideForm = z.infer<typeof brandGuideSchema>;

// Estimated token cost for brand extraction with GPT-4 Vision (includes imagery + component + gradients)
const BRAND_EXTRACTION_TOKEN_COST = 4500; // Was 4200, now +300 for gradient detection

export default function BrandSettingsPage() {
  const { t } = useTranslation();
  const { workspace } = useAuthStore();
  const [brandGuide, setBrandGuide] = useState<BrandGuide | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [extractionJobId, setExtractionJobId] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [tokenCostRange, setTokenCostRange] = useState<{ min: number; max: number; medium: number } | null>(null);

  // Get active website from workspace
  const activeWebsite = workspace?.websites?.find((w) => w.isActive);

  // Use brand extraction status hook
  const extractionStatus = useBrandExtractionStatus(extractionJobId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BrandGuideForm>({
    resolver: zodResolver(brandGuideSchema),
  });

  // Fetch brand guide on mount
  useEffect(() => {
    async function fetchBrandGuide() {
      try {
        const response = await apiClient.get('/brand/guide');
        const guide = response.data;
        setBrandGuide(guide);

        // Set form defaults
        if (guide) {
          reset({
            companyDescription: guide.companyDescription || '',
            brandKeywords: guide.brandKeywords || [],
          });
        }
      } catch (err: any) {
        console.error('Failed to fetch brand guide:', err);

        // 404 is expected when no brand guide exists yet - show empty state instead
        if (err.response?.status !== 404) {
          setError('Failed to load brand guide');
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrandGuide();
  }, [reset]);

  // Fetch token cost range on mount
  useEffect(() => {
    async function fetchCostRange() {
      try {
        const response = await apiClient.get('/brand/extract/cost-estimate');
        if (response.data.success) {
          setTokenCostRange(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch cost range:', err);
        // Fallback to default values if API fails
        setTokenCostRange({ min: 2236, max: 8884, medium: 4583 });
      }
    }

    fetchCostRange();
  }, []);

  const onSubmit = async (data: BrandGuideForm) => {
    if (!brandGuide) return;

    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const response = await apiClient.patch(`/brand/guide/${brandGuide.id}`, data);
      setBrandGuide(response.data);
      setSuccessMessage('Brand guide updated successfully');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to update brand guide:', err);
      setError(err.response?.data?.message || 'Failed to update brand guide');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle config modal submit (opens token cost modal)
  const handleConfigSubmit = () => {
    if (!activeWebsite) {
      setError('No active website found. Please select a website first.');
      return;
    }

    // Clear error and proceed
    setError('');

    // Close config modal and open token cost modal
    setShowConfigModal(false);
    setShowRefreshModal(true);
  };

  // Execute brand refresh (async)
  const handleConfirmRefresh = async () => {
    if (!activeWebsite) {
      setError('No active website found. Please select a website first.');
      throw new Error('No active website');
    }

    setError('');
    setSuccessMessage('');

    try {
      // Use active website URL
      let url = activeWebsite.domain;
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      // Start async brand extraction job
      const response = await apiClient.post('/brand/extract-async', {
        url: url,
      });

      const { jobId } = response.data.data;
      setExtractionJobId(jobId);

      setSuccessMessage('Brand extraction started. This will take 20-30 seconds...');
    } catch (err: any) {
      console.error('Failed to start brand extraction:', err);
      setError(err.response?.data?.message || 'Failed to start brand extraction');
      throw err; // Re-throw to let TokenCostModal handle it
    }
  };

  // Handle extraction completion
  useEffect(() => {
    if (extractionStatus.status?.state === 'completed') {
      // Fetch updated brand guide
      async function refreshBrandGuide() {
        try {
          const response = await apiClient.get('/brand/guide');
          const guide = response.data;
          setBrandGuide(guide);

          // Update form defaults
          reset({
            companyDescription: guide.companyDescription || '',
            brandKeywords: guide.brandKeywords || [],
          });

          // Refresh website limit (count may have changed)
          try {
            const limitResponse = await apiClient.get('/brand/extract/can-add-website');
            if (limitResponse.data.success) {
              setWebsiteLimit(limitResponse.data.data);
            }
          } catch (err) {
            console.error('Failed to refresh website limit:', err);
          }

          setSuccessMessage('Brand guide successfully refreshed with AI analysis');
          setExtractionJobId(null);

          // Clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
          console.error('Failed to fetch updated brand guide:', err);
          setError('Extraction completed but failed to load updated guide');
        }
      }

      refreshBrandGuide();
    } else if (extractionStatus.status?.state === 'failed') {
      setError(extractionStatus.status.error || 'Brand extraction failed');
      setExtractionJobId(null);
    }
  }, [extractionStatus.status?.state, reset]);

  // Handle cancel
  const handleCancel = async () => {
    try {
      await extractionStatus.cancel();
      setExtractionJobId(null);
      setSuccessMessage('Brand extraction cancelled');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel extraction');
    }
  };

  // Handle prioritize
  const handleConfirmPrioritize = async () => {
    try {
      await extractionStatus.prioritize();
      setSuccessMessage('Brand extraction prioritized');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to prioritize extraction');
      throw err; // Re-throw for modal
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">Brand Guide</h1>
          <p className="text-gray-600 dark:text-gray-400">Loading brand guide...</p>
        </div>
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">
            Brand Guide
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage your visual brand identity
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowConfigModal(true)}
            variant="primary"
            disabled={!!extractionJobId || isEditing}
            title={brandGuide ? "Extract brand again with AI (costs tokens)" : "Extract brand guide with AI"}
          >
            {extractionJobId ? (
              'Extracting...'
            ) : brandGuide ? (
              `Refresh (${BRAND_EXTRACTION_TOKEN_COST.toLocaleString()} tokens)`
            ) : (
              'Extract Brand Guide'
            )}
          </Button>
          {brandGuide && !isEditing && (
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Empty State - No Brand Guide */}
      {!brandGuide && !extractionJobId && (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎨</div>
            <h3 className="text-xl font-bold mb-2 dark:text-white">
              No Brand Guide Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Extract your brand guide from a website using AI. GPT-4 Vision will analyze the design and generate your color palette, typography, and visual style.
            </p>
            <Button
              onClick={() => setShowConfigModal(true)}
              variant="primary"
              size="lg"
            >
              Extract Brand Guide
            </Button>
          </div>
        </Card>
      )}

      {/* Extraction Progress */}
      {extractionJobId && extractionStatus.status && (
        <div className="mb-6 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Minimal spinner */}
                {(extractionStatus.status.state === 'waiting' || extractionStatus.status.state === 'active') && (
                  <div className="w-4 h-4 border-2 border-gray-900 dark:border-white border-t-transparent dark:border-t-transparent rounded-full animate-spin"></div>
                )}
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {extractionStatus.status.state === 'waiting' && 'Brand Extraction Queued'}
                  {extractionStatus.status.state === 'active' && 'Extracting Brand...'}
                  {extractionStatus.status.state === 'completed' && 'Brand Extracted!'}
                  {extractionStatus.status.state === 'failed' && 'Extraction Failed'}
                  {extractionStatus.status.state === 'delayed' && 'Extraction Delayed'}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {(() => {
                    if (!extractionStatus.status.createdAt) return '0s';
                    const elapsed = Math.floor((Date.now() - new Date(extractionStatus.status.createdAt).getTime()) / 1000);
                    const minutes = Math.floor(elapsed / 60);
                    const seconds = elapsed % 60;
                    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
                  })()}
                </span>
                {/* Prioritize button - only show if waiting and position > 1 */}
                {extractionStatus.status.state === 'waiting' && extractionStatus.status.queuePosition && extractionStatus.status.queuePosition > 1 && (
                  <button
                    type="button"
                    onClick={() => setShowPriorityModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white border-2 border-gray-900 dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                  >
                    Skip the Line
                  </button>
                )}
                {/* Cancel button - only show if waiting */}
                {extractionStatus.status.state === 'waiting' && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-950 border-2 border-gray-200 dark:border-gray-800 hover:border-gray-900 dark:hover:border-white transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {(extractionStatus.status.state === 'waiting' || extractionStatus.status.state === 'active') && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                  <span>Progress</span>
                  <span className="tabular-nums">{extractionStatus.status.progress || 0}%</span>
                </div>
                <div className="w-full h-1 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-900 dark:bg-white transition-all duration-500 ease-out"
                    style={{ width: `${extractionStatus.status.progress || 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Description */}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {extractionStatus.status.state === 'waiting' && 'Your brand extraction is queued. This usually takes 1-2 minutes.'}
              {extractionStatus.status.state === 'active' && 'GPT-4o Vision is analyzing your website and extracting brand elements.'}
              {extractionStatus.status.state === 'completed' && 'Your brand has been successfully extracted and is now available.'}
              {extractionStatus.status.state === 'failed' && 'Something went wrong during extraction.'}
            </p>

            {/* Queue information */}
            {extractionStatus.status.state === 'waiting' && extractionStatus.status.queuePosition && (
              <div className="flex items-center gap-6 text-sm border-t border-gray-100 dark:border-gray-900 pt-3 mt-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    Queue position
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium tabular-nums">
                    #{extractionStatus.status.queuePosition}
                  </span>
                  {extractionStatus.status.totalInQueue && (
                    <span className="text-gray-400 dark:text-gray-600 text-xs">
                      (of {extractionStatus.status.totalInQueue})
                    </span>
                  )}
                </div>
                {extractionStatus.status.estimatedStartTime && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">
                      Starts in
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium tabular-nums">
                      {(() => {
                        const timeUntil = Math.floor((new Date(extractionStatus.status.estimatedStartTime).getTime() - Date.now()) / 1000);
                        if (timeUntil <= 0) return 'Starting soon...';
                        const minutes = Math.floor(timeUntil / 60);
                        const seconds = timeUntil % 60;
                        return minutes > 0 ? `~${minutes}m ${seconds}s` : `~${seconds}s`;
                      })()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {error && (
        <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200 text-sm">
          {successMessage}
        </div>
      )}

      {/* Brand Guide Content - Only show if brand guide exists */}
      {brandGuide && (
        <>
          {/* Logo Section */}
          {brandGuide.logoUrl && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Logo</h2>
          <div className="flex items-center gap-6">
            <img
              src={brandGuide.logoUrl}
              alt="Brand logo"
              className="max-w-xs max-h-32 object-contain bg-gray-50 dark:bg-gray-800 p-4 rounded"
            />
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              {brandGuide.logoWidth && brandGuide.logoHeight && (
                <p>Dimensions: {brandGuide.logoWidth}x{brandGuide.logoHeight}px</p>
              )}
              {brandGuide.logoFileSize && (
                <p>Size: {formatFileSize(brandGuide.logoFileSize)}</p>
              )}
              {brandGuide.logoMimeType && (
                <p>Type: {brandGuide.logoMimeType}</p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Color Palette */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">Color Palette</h2>

        {/* Primary Colors */}
        {brandGuide.primaryColors && brandGuide.primaryColors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 dark:text-white">Primary Colors</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {brandGuide.primaryColors.map((color, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className="w-full h-20 rounded-lg shadow-md"
                    style={{ backgroundColor: color.hex }}
                  ></div>
                  <p className="mt-2 text-sm font-medium dark:text-white">
                    {color.name || `Color ${index + 1}`}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {color.hex.toUpperCase()}
                  </p>
                  {color.usage !== undefined && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {typeof color.usage === 'number' ? `Usage: ${color.usage}%` : color.usage}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Secondary Colors */}
        {brandGuide.secondaryColors && brandGuide.secondaryColors.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 dark:text-white">Secondary Colors</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {brandGuide.secondaryColors.map((color, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className="w-full h-20 rounded-lg shadow-md"
                    style={{ backgroundColor: color.hex }}
                  ></div>
                  <p className="mt-2 text-sm font-medium dark:text-white">
                    {color.name || `Color ${index + 1}`}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {color.hex.toUpperCase()}
                  </p>
                  {color.usage !== undefined && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {typeof color.usage === 'number' ? `Usage: ${color.usage}%` : color.usage}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!brandGuide.primaryColors?.length && !brandGuide.secondaryColors?.length && (
          <p className="text-gray-500 dark:text-gray-400">No colors extracted</p>
        )}
      </Card>

      {/* Gradients */}
      {brandGuide.gradients && brandGuide.gradients.length > 0 && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Gradients</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {brandGuide.gradients.map((gradient, index) => (
              <div key={index} className="border-2 border-black dark:border-white p-4">
                {/* Gradient Preview */}
                <div
                  className="w-full h-32 mb-4 border-2 border-black dark:border-white"
                  style={{ background: gradient.css }}
                ></div>

                {/* Gradient Details */}
                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-sm dark:text-white">Type:</span>
                    <span className="ml-2 text-sm dark:text-gray-300">{gradient.type}</span>
                  </div>

                  <div>
                    <span className="font-bold text-sm dark:text-white">Colors:</span>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {gradient.colors.map((color, colorIndex) => (
                        <div key={colorIndex} className="flex items-center gap-1">
                          <div
                            className="w-6 h-6 border-2 border-black dark:border-white"
                            style={{ backgroundColor: color }}
                          ></div>
                          <span className="text-xs dark:text-gray-400">{color.toUpperCase()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {gradient.direction && (
                    <div>
                      <span className="font-bold text-sm dark:text-white">Direction:</span>
                      <span className="ml-2 text-sm dark:text-gray-300">{gradient.direction}</span>
                    </div>
                  )}

                  <div>
                    <span className="font-bold text-sm dark:text-white">Usage:</span>
                    <span className="ml-2 text-sm dark:text-gray-300">{gradient.usage}</span>
                  </div>

                  {/* CSS Copy Box */}
                  <div className="mt-3 pt-3 border-t-2 border-black dark:border-white">
                    <span className="font-bold text-xs dark:text-white block mb-1">CSS:</span>
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 p-2 block font-mono border-2 border-black dark:border-white break-all">
                      {gradient.css}
                    </code>
                  </div>

                  {gradient.stops && gradient.stops.length > 0 && (
                    <div>
                      <span className="font-bold text-xs dark:text-white">Stops:</span>
                      <span className="ml-2 text-xs dark:text-gray-400">{gradient.stops.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Typography */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">Typography</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Heading Font */}
          {brandGuide.typographyHeading && (
            <div>
              <h3 className="text-lg font-semibold mb-2 dark:text-white">Heading Font</h3>
              <div className="space-y-2 text-sm">
                {typeof brandGuide.typographyHeading === 'string' ? (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Font:</span> {brandGuide.typographyHeading}
                  </p>
                ) : (
                  <>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Family:</span> {brandGuide.typographyHeading.family}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Weights:</span> {brandGuide.typographyHeading.weights.join(', ')}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Fallbacks:</span> {brandGuide.typographyHeading.fallbacks.join(', ')}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Body Font */}
          {brandGuide.typographyBody && (
            <div>
              <h3 className="text-lg font-semibold mb-2 dark:text-white">Body Font</h3>
              <div className="space-y-2 text-sm">
                {typeof brandGuide.typographyBody === 'string' ? (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Font:</span> {brandGuide.typographyBody}
                  </p>
                ) : (
                  <>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Family:</span> {brandGuide.typographyBody.family}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Weights:</span> {brandGuide.typographyBody.weights.join(', ')}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Fallbacks:</span> {brandGuide.typographyBody.fallbacks.join(', ')}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {!brandGuide.typographyHeading && !brandGuide.typographyBody && (
          <p className="text-gray-500 dark:text-gray-400">No typography extracted</p>
        )}
      </Card>

      {/* Imagery Guidelines */}
      {brandGuide.imageryGuidelines && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Imagery Guidelines</h2>
          <div className="space-y-6">
            {/* Art Direction */}
            <div>
              <h3 className="text-sm font-bold mb-2 dark:text-white uppercase tracking-wide">Art Direction</h3>
              <p className="text-gray-700 dark:text-gray-300">{brandGuide.imageryGuidelines.artDirection}</p>
            </div>

            {/* Preferred Subjects */}
            {brandGuide.imageryGuidelines.subjects && brandGuide.imageryGuidelines.subjects.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Preferred Subjects</h3>
                <div className="flex flex-wrap gap-2">
                  {brandGuide.imageryGuidelines.subjects.map((subject, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-white dark:bg-gray-700 border-2 border-black dark:border-white text-sm font-medium dark:text-white"
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Background Treatments */}
            {brandGuide.imageryGuidelines.backgrounds && brandGuide.imageryGuidelines.backgrounds.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Background Treatments</h3>
                <div className="flex flex-wrap gap-2">
                  {brandGuide.imageryGuidelines.backgrounds.map((background, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-white dark:bg-gray-700 border-2 border-black dark:border-white text-sm font-medium dark:text-white"
                    >
                      {background}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Aspect Ratios */}
            {brandGuide.imageryGuidelines.aspectRatios && brandGuide.imageryGuidelines.aspectRatios.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Aspect Ratios</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {brandGuide.imageryGuidelines.aspectRatios.map((ar, index) => (
                    <div
                      key={index}
                      className="border-2 border-black dark:border-white p-4 text-center bg-white dark:bg-gray-800"
                    >
                      <div className="text-lg font-bold mb-1 dark:text-white">{ar.ratio}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{ar.platform}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Color Treatment */}
            <div>
              <h3 className="text-sm font-bold mb-2 dark:text-white uppercase tracking-wide">Color Treatment</h3>
              <p className="text-gray-700 dark:text-gray-300">{brandGuide.imageryGuidelines.colorTreatment}</p>
            </div>

            {/* Filters (if any) */}
            {brandGuide.imageryGuidelines.filters && brandGuide.imageryGuidelines.filters.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Filters & Effects</h3>
                <ul className="space-y-1.5">
                  {brandGuide.imageryGuidelines.filters.map((filter, index) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{filter}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Component Styles */}
      {brandGuide.componentStyles && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Components</h2>
          <div className="space-y-6">
            {/* Buttons */}
            <div>
              <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Buttons</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Variants</p>
                  <div className="flex flex-wrap gap-2">
                    {brandGuide.componentStyles.buttons.variants.map((variant, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-white dark:bg-gray-700 border-2 border-black dark:border-white text-sm font-medium dark:text-white capitalize"
                      >
                        {variant}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Text Case</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.buttons.textCase}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Padding</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.buttons.padding}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Corners</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.buttons.corners}</p>
                </div>
              </div>
            </div>

            {/* Icons */}
            <div>
              <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Icons</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Style</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.icons.style}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Stroke Width</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.icons.strokeWidth}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Corners</p>
                  <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.icons.corners}</p>
                </div>
              </div>
            </div>

            {/* Border Radius */}
            <div>
              <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Radius</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="border-2 border-black dark:border-white p-4 text-center bg-white dark:bg-gray-800">
                  <div className="text-lg font-bold mb-1 dark:text-white">{brandGuide.componentStyles.radius.buttons}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Buttons</div>
                </div>
                <div className="border-2 border-black dark:border-white p-4 text-center bg-white dark:bg-gray-800">
                  <div className="text-lg font-bold mb-1 dark:text-white">{brandGuide.componentStyles.radius.cards}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Cards</div>
                </div>
                <div className="border-2 border-black dark:border-white p-4 text-center bg-white dark:bg-gray-800">
                  <div className="text-lg font-bold mb-1 dark:text-white">{brandGuide.componentStyles.radius.inputs}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Inputs</div>
                </div>
              </div>
            </div>

            {/* Shadows (optional) */}
            {brandGuide.componentStyles.shadows && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Shadows</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Elevation</p>
                    <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.shadows.elevation}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Pattern</p>
                    <p className="text-gray-700 dark:text-gray-300 font-mono text-xs">{brandGuide.componentStyles.shadows.pattern}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Spacing (optional) */}
            {brandGuide.componentStyles.spacing && (
              <div>
                <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">Spacing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Grid Gap</p>
                    <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.spacing.gridGap}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Section Padding</p>
                    <p className="text-gray-700 dark:text-gray-300">{brandGuide.componentStyles.spacing.sectionPadding}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Tone of Voice */}
      {brandGuide.toneOfVoice && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Tone of Voice</h2>
          <div className="space-y-4">
            {Object.entries(brandGuide.toneOfVoice)
              .filter(([key]) => key !== 'description')
              .map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium capitalize dark:text-white">
                      {key}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {value}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(value / 10) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            {brandGuide.toneOfVoice.description && (
              <p className="mt-4 text-sm text-gray-700 dark:text-gray-300">
                {brandGuide.toneOfVoice.description}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Company Description & Keywords (Editable) */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold mb-4 dark:text-white">Company Description</h2>
            {isEditing ? (
              <Textarea
                {...register('companyDescription')}
                label="Description"
                rows={4}
                error={errors.companyDescription?.message}
                placeholder="20-600 characters"
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300">
                {brandGuide.companyDescription || 'No description available'}
              </p>
            )}
          </div>

          <div>
            <h2 className="text-xl font-bold mb-4 dark:text-white">Brand Keywords</h2>
            {isEditing ? (
              <Input
                {...register('brandKeywords')}
                label="Keywords (comma-separated)"
                placeholder="innovation, quality, customer-focused"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {brandGuide.brandKeywords && brandGuide.brandKeywords.length > 0 ? (
                  brandGuide.brandKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {keyword}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No keywords defined</p>
                )}
              </div>
            )}
          </div>

          {isEditing && (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form>
      </Card>

      {/* Extraction Metadata */}
      {brandGuide.extractionMetadata && (
        <Card>
          <h2 className="text-xl font-bold mb-4 dark:text-white">Extraction Details</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium dark:text-white">Method:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {brandGuide.extractionMetadata.method}
              </span>
            </div>
            <div>
              <span className="font-medium dark:text-white">Confidence:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {brandGuide.extractionMetadata.confidence}%
              </span>
            </div>
            <div>
              <span className="font-medium dark:text-white">Extracted:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {formatDate(brandGuide.extractionMetadata.timestamp)}
              </span>
            </div>
            <div>
              <span className="font-medium dark:text-white">Source:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {brandGuide.sourceUrl || 'N/A'}
              </span>
            </div>
            {brandGuide.extractionMetadata.siteName && (
              <div>
                <span className="font-medium dark:text-white">Site Name:</span>{' '}
                <span className="text-gray-700 dark:text-gray-300">
                  {brandGuide.extractionMetadata.siteName}
                </span>
              </div>
            )}
            {brandGuide.extractionMetadata.model && (
              <div>
                <span className="font-medium dark:text-white">AI Model:</span>{' '}
                <span className="text-gray-700 dark:text-gray-300">
                  {brandGuide.extractionMetadata.model}
                </span>
              </div>
            )}
          </div>

          {brandGuide.extractionMetadata.visualStyle && (
            <div className="mt-4">
              <span className="font-medium dark:text-white">Visual Style:</span>{' '}
              <p className="text-gray-700 dark:text-gray-300 mt-1">
                {brandGuide.extractionMetadata.visualStyle}
              </p>
            </div>
          )}

          {brandGuide.extractionMetadata.reasoning && (
            <div className="mt-4">
              <span className="font-medium dark:text-white">AI Reasoning:</span>{' '}
              <p className="text-gray-700 dark:text-gray-300 mt-1">
                {brandGuide.extractionMetadata.reasoning}
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Version Info */}
      <Card>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Version {brandGuide.version} • Last updated {formatDate(brandGuide.updatedAt)}
            </p>
          </div>
          <Button variant="secondary" onClick={() => (window.location.href = '/brand/guide/history')}>
            View History
          </Button>
        </div>
      </Card>
        </>
      )}

      {/* Website URL Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-3 dark:text-white">
              Refresh Brand Guide
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Re-analyze your website to update the brand guide with the latest design and content changes.
            </p>

            <div className="space-y-4 mb-4">
              {/* Active Website Display */}
              <div>
                <label className="block text-sm font-medium mb-2 dark:text-white">
                  {t('settings.brand.activeWebsite', 'Active Website')}
                </label>
                {activeWebsite ? (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-black dark:border-white rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 text-2xl">🌐</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {activeWebsite.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate">
                          {activeWebsite.domain}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                    <p className="text-sm text-red-900 dark:text-red-200">
                      {t('settings.brand.noActiveWebsite', 'No active website selected. Please add and select a website first.')}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">
                  📧 Email Notification
                </p>
                <p className="text-blue-800 dark:text-blue-300 text-xs">
                  You will receive an email when the extraction is complete (typically 20-30 seconds).
                </p>
              </div>
            </div>

            {error && (
              <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfigModal(false);
                  setError('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleConfigSubmit} disabled={!activeWebsite}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Token Cost Modal - Refresh */}
      <TokenCostModal
        open={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        onConfirm={handleConfirmRefresh}
        title={t('tokenCostModal.brandExtraction.title')}
        description={
          tokenCostRange
            ? `${t('tokenCostModal.brandExtraction.description')} Token cost varies from ${tokenCostRange.min.toLocaleString()} (short pages) to ${tokenCostRange.max.toLocaleString()} (long pages). Average: ${tokenCostRange.medium.toLocaleString()} tokens.`
            : t('tokenCostModal.brandExtraction.description')
        }
        tokenCost={tokenCostRange?.medium || BRAND_EXTRACTION_TOKEN_COST}
        confirmButtonText={t('tokenCostModal.brandExtraction.confirm')}
        estimatedDuration={t('tokenCostModal.brandExtraction.duration')}
      />

      {/* Token Cost Modal - Prioritize */}
      <TokenCostModal
        open={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        onConfirm={handleConfirmPrioritize}
        title="Prioritize Brand Extraction"
        description="Move your brand extraction job to the front of the queue. Your extraction will start immediately instead of waiting in line."
        tokenCost={3500}
        confirmButtonText="Prioritize Now"
        estimatedDuration="Immediate start"
      />
    </div>
  );
}
